import {
  viewSessionNotes, SessionNotesView, sessionServiceOptionsDict,
  sessionParticipantOptionsDict,
  getCptCode,
  getDiagnosisByIds,
  AppointmentType,
  PsychiatryNote,
  Session,
  Appointment,
} from '@uplift/api';
import { useQuery } from '@tanstack/react-query';

export type ClinicalNotesModalStateType = {
  type: AppointmentType;
  isLegacy: boolean;
  appointment: Appointment;
  session: {
    clinicalNote: string | null;
    id: string;
    cpts: {
      name: string;
      code: string;
      id: string;
    }[];
    diagnosis: {
      name: string;
      code: string;
      id: string;
    }[];
    serviceType?: string;
    participant?: string;
    submittedAt: string;
  };
  sessionNotesView?: SessionNotesView;
  psychiatryNote?: PsychiatryNote;
};

export const useClinicalNotesModal = (appointment?: Appointment, session?: Session, enabled = true) => {
  const { data: state } = useQuery({
    queryKey: [ 'appointment-clinical-notes', appointment?.id, session?.id ] as const,
    queryFn: async (): Promise<ClinicalNotesModalStateType | null> => {
      if (!appointment) {
        return null;
      }

      if (!session) {
        return null;
      }

      const sessionDetails = await fetchSession(session);

      // if viewSessionNotes throws an error, then it's a legacy session note
      try {
        const sessionNotesView = await viewSessionNotes(session.id);

        if (appointment) {
          if (sessionNotesView.data.type === AppointmentType.therapy) {
            return { type: sessionNotesView.data.type, appointment, session: sessionDetails, isLegacy: false, sessionNotesView: sessionNotesView.data.note };
          } else if (sessionNotesView.data.type === AppointmentType.psychiatry) {
            return { type: sessionNotesView.data.type, appointment, session: sessionDetails, isLegacy: false, psychiatryNote: sessionNotesView.data.note };
          }
        }

      } catch (error) {
        if (appointment) {
          return { type: appointment.type, appointment, session: sessionDetails, isLegacy: true, sessionNotesView: undefined, psychiatryNote: undefined };
        }
      }

      return null;
    },
    enabled: enabled && !!appointment?.id,
  });

  return { state };
};

const fetchSession = async (session: Session): Promise<ClinicalNotesModalStateType['session']> => {
  const [ diagnosis, ...cptCodes ] = await Promise.all([
    getDiagnosisByIds(session.diagnosisIds),
    ...[ session?.cptCodeId, ...session.addonCptCodeIds ].filter(Boolean).map((c) => getCptCode(c)),
  ]);
  const serviceType = session.serviceType ? sessionServiceOptionsDict[session.serviceType] : null;
  const participant = session.participation ? sessionParticipantOptionsDict[session.participation] : null;

  return {
    id: session.id,
    cpts: cptCodes.map(cpt => ({
      id: cpt?.data?.id as string,
      name: cpt?.data?.name as string,
      code: cpt?.data?.code as string,
    })),
    clinicalNote: session.clinicalNote ?? null,
    diagnosis: diagnosis.data,
    serviceType: serviceType ?? undefined,
    participant: participant ?? undefined,
    submittedAt: session.submittedAt as string,
  };
};
